/**
 * v2.0.1 NotifyReport — CP → CSMS report payload (OCPP 2.0.1 §B.07).
 *
 * Triggered by an inbound `GetBaseReport` (csmsCallHandler.ts dispatches
 * us to fire the report after replying `Accepted`). The CP sends one or
 * more NotifyReport messages chunking the data; for our minimum
 * inventory (10 configuration variables) a single message with
 * `tbc: false` is enough.
 *
 * `reportData` is an array of `ReportDataType` (per the vendored
 * schema): each item carries a `component`, `variable`, and at least
 * one `variableAttribute`. We attach `variableCharacteristics` too —
 * it's optional in the schema but many CSMS implementations use it to
 * render the inventory tree.
 */
import { ConfigurationKeyType, type ConfigurationSection } from '@/domain/types';
import type { ConfigurationKeyDescriptor } from '@/protocol/OcppAdapter';

export type V201DataType =
  | 'string'
  | 'decimal'
  | 'integer'
  | 'dateTime'
  | 'boolean'
  | 'OptionList'
  | 'SequenceList'
  | 'MemberList';

/**
 * Map our v16-shaped `ConfigurationKeyType` onto the v2.0.1
 * `DataEnumType`. CSL becomes MemberList (the closest match — the
 * value is a comma-separated list of pre-defined members).
 */
function toV201DataType(t: ConfigurationKeyType): V201DataType {
  switch (t) {
    case ConfigurationKeyType.Integer:
      return 'integer';
    case ConfigurationKeyType.Boolean:
      return 'boolean';
    case ConfigurationKeyType.CSL:
      return 'MemberList';
    case ConfigurationKeyType.String:
    default:
      return 'string';
  }
}

/**
 * Split a flat "Component.Variable" key (the Session 10d storage
 * shortcut) back into its two halves. Variables without a dot are
 * treated as belonging to a synthetic "Custom" component so the wire
 * frame stays well-formed.
 */
export function splitComponentVariable(flatKey: string): {
  component: string;
  variable: string;
} {
  const dot = flatKey.indexOf('.');
  if (dot < 0 || dot === flatKey.length - 1) {
    return { component: 'Custom', variable: flatKey };
  }
  return {
    component: flatKey.slice(0, dot),
    variable: flatKey.slice(dot + 1),
  };
}

export interface ReportData {
  readonly component: { readonly name: string };
  readonly variable: { readonly name: string };
  readonly variableAttribute: readonly {
    readonly type: 'Actual';
    readonly value: string;
    readonly mutability: 'ReadOnly' | 'ReadWrite';
    readonly persistent: boolean;
    readonly constant: boolean;
  }[];
  readonly variableCharacteristics: {
    readonly dataType: V201DataType;
    readonly supportsMonitoring: boolean;
  };
}

export interface NotifyReportDomain {
  readonly requestId: number;
  readonly generatedAt: string;
  readonly seqNo: number;
  /** "to be continued" — set to false on the last chunk. */
  readonly tbc: boolean;
  readonly reportData: readonly ReportData[];
}

export function buildNotifyReportRequest(
  req: NotifyReportDomain,
): Record<string, unknown> {
  return {
    requestId: req.requestId,
    generatedAt: req.generatedAt,
    seqNo: req.seqNo,
    tbc: req.tbc,
    reportData: req.reportData.map((rd) => ({
      component: { name: rd.component.name },
      variable: { name: rd.variable.name },
      variableAttribute: rd.variableAttribute.map((va) => ({
        type: va.type,
        value: va.value,
        mutability: va.mutability,
        persistent: va.persistent,
        constant: va.constant,
      })),
      variableCharacteristics: {
        dataType: rd.variableCharacteristics.dataType,
        supportsMonitoring: rd.variableCharacteristics.supportsMonitoring,
      },
    })),
  };
}

/**
 * Build a single `ReportData` row from one of our flattened
 * configuration descriptors. The `section` field on the descriptor is
 * intentionally not surfaced in the wire frame — v2.0.1 groups
 * variables by `component.name`, not by section.
 */
export function descriptorToReportData(
  d: ConfigurationKeyDescriptor & { section?: ConfigurationSection },
): ReportData {
  const { component, variable } = splitComponentVariable(d.key);
  return {
    component: { name: component },
    variable: { name: variable },
    variableAttribute: [
      {
        type: 'Actual',
        value: d.value,
        mutability: d.readonly ? 'ReadOnly' : 'ReadWrite',
        persistent: true,
        constant: d.readonly,
      },
    ],
    variableCharacteristics: {
      dataType: toV201DataType(d.type),
      supportsMonitoring: false,
    },
  };
}

/**
 * Session 19b — build a `ReportData` row straight from a structured
 * Device Model row, preserving the real `dataType` + mutability instead
 * of round-tripping through the flat ConfigurationKeyType.
 */
export function variableToReportData(v: {
  component: string;
  variable: string;
  value: string;
  mutability: string;
  dataType: string;
}): ReportData {
  const readOnly = v.mutability === 'ReadOnly';
  return {
    component: { name: v.component },
    variable: { name: v.variable },
    variableAttribute: [
      {
        type: 'Actual',
        value: v.value,
        mutability: readOnly ? 'ReadOnly' : 'ReadWrite',
        persistent: true,
        constant: readOnly,
      },
    ],
    variableCharacteristics: {
      dataType: (v.dataType as V201DataType) ?? 'string',
      supportsMonitoring: false,
    },
  };
}

/**
 * Session 19b — split a full reportData set into paginated NotifyReport
 * payloads. `maxItemsPerPage` derives from the OCPPCommCtrlr
 * `MessageMaxReportLength` Variable (interpreted as a max item count — a
 * documented simplification; the spec measures bytes). Each page gets an
 * incrementing seqNo and `tbc: true` except the last. An empty input
 * still yields one page (empty reportData, tbc false) so the CSMS always
 * gets a terminating frame.
 */
export function paginateNotifyReport(
  requestId: number,
  reportData: readonly ReportData[],
  maxItemsPerPage: number,
  generatedAt: string = new Date().toISOString(),
): readonly NotifyReportDomain[] {
  const perPage = Math.max(1, Math.floor(maxItemsPerPage));
  const pages: NotifyReportDomain[] = [];
  if (reportData.length === 0) {
    return [{ requestId, generatedAt, seqNo: 0, tbc: false, reportData: [] }];
  }
  for (let i = 0; i < reportData.length; i += perPage) {
    const chunk = reportData.slice(i, i + perPage);
    const isLast = i + perPage >= reportData.length;
    pages.push({
      requestId,
      generatedAt,
      seqNo: pages.length,
      tbc: !isLast,
      reportData: chunk,
    });
  }
  return pages;
}
